import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Card, CardContent, Chip, Link, Stack, Typography } from '@mui/material';
import {
  CREDENTIAL_STATE_LABEL,
  credentialStateColor,
  formatInterval,
  groupRegistryByProvider,
  isOnDemand,
  stalledLabel,
  type RegistryRow,
} from './apiRegistryView';
import { INGEST_LANE_LABEL, laneChipColor, type IngestLane } from './ingestHonesty';

export type ApiRegistryCardRow = RegistryRow & {
  runtime?: { lane?: IngestLane; latest_ingest_at?: string | null };
};

type ApiRegistryCardsProps = {
  rows: ApiRegistryCardRow[];
  onSelect: (apiId: string) => void;
};

function formatWhen(value?: string | null) {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Never' : date.toLocaleString();
}

export default function ApiRegistryCards({ rows, onSelect }: ApiRegistryCardsProps) {
  const cards = groupRegistryByProvider(rows);
  if (!cards.length) {
    return <Alert severity="info">No APIs matched the current filters.</Alert>;
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {cards.length} {cards.length === 1 ? 'provider' : 'providers'}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: '1fr 1fr 1fr' },
          gap: 2,
        }}
      >
        {cards.map((card) => (
          <Card key={card.provider} variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {card.provider}
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                {card.apis.map(({ row, apps, toMerge }) => {
                  const lane = (row as ApiRegistryCardRow).runtime?.lane;
                  const credential = row.registry?.credential_state;
                  const onDemand = isOnDemand(row);
                  return (
                    <Box
                      key={row.api_id}
                      onClick={() => onSelect(row.api_id)}
                      sx={{ cursor: 'pointer', borderTop: '1px solid', borderColor: 'divider', pt: 1.5 }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                            {row.api_name || row.api_id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace' }}>
                            {row.api_id}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          {toMerge && <Chip size="small" label="to merge" color="warning" />}
                          {lane && <Chip size="small" label={INGEST_LANE_LABEL[lane]} color={laneChipColor(lane)} />}
                        </Stack>
                      </Stack>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                        {apps.length ? apps.map((app) => (
                          <Stack key={app.app_id} direction="row" spacing={0.5} alignItems="center">
                            <Link
                              component={RouterLink}
                              to={`/apps/${encodeURIComponent(app.app_id)}`}
                              variant="caption"
                              onClick={(event) => event.stopPropagation()}
                            >
                              {app.app_name}
                            </Link>
                            {app.feed === false && (
                              <Typography variant="caption" color="text.secondary">connected, no feed</Typography>
                            )}
                          </Stack>
                        )) : (
                          <Typography variant="caption" color="text.secondary">No app</Typography>
                        )}
                      </Stack>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                        {credential && (
                          <Chip size="small" label={CREDENTIAL_STATE_LABEL[credential]} color={credentialStateColor(credential)} variant="outlined" />
                        )}
                        {row.registry?.stalled && (
                          <Chip size="small" label={stalledLabel(row.registry.stalled_reason)} color="error" />
                        )}
                        <Chip size="small" variant="outlined" label={onDemand ? 'on demand' : formatInterval(row)} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                        Last run: {formatWhen(row.registry?.last_run?.at || (row as ApiRegistryCardRow).runtime?.latest_ingest_at)}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
